import { logger } from '@springtree/eva-sdk-core-logger';
import {
  Interceptor,
  IResponseInterceptor,
} from '@springtree/eva-sdk-core-service';
import { isFunction } from 'lodash';
import { Store } from 'redux';
import { createCurrentUserTokenAction, IEvaUserTokenExpiredAction } from '../eva-sdk-redux-store-builder';

/**
 * This interceptor listens to service responses for a 401 status and checks
 * if the current user token needs to be unset because it has expired
 *
 * @export
 * @class UserExpiredInterceptor
 */
export class UserExpiredInterceptor extends Interceptor {

  /**
   * Creates an instance of the EvaAvailableServices interceptor
   */
  constructor(
    private store: Store,
    private userTokenExpired?: (userToken: string) => void,
  ) {
    super('EVA:ReduxStoreBuildUserExpired');
    this.priority = 5;
  }

  /**
   * The response interceptor implementation.
   * Available as a public property for direct use
   * By returning a fetch Response we will prevent the service call from going out
   *
   * @type {IResponseInterceptor}
   */
  public responseInterceptor: IResponseInterceptor = {
    hook: async (request, options, response) => {

      if (response.status === 401) {
        const storeState = this.store.getState();
        const storeToken = storeState.currentUserToken;
        const token = request.headers.get('Authorization');
        const serviceName = request.headers.get('EVA-Service-Name');
        const isLogin = serviceName === 'Core:Login';

        // Check the token is for this store
        // We can have multiple stores active with their service interceptors
        // Exclude the login service explicitly
        //
        if (!isLogin && token && token === storeToken) {
          logger.log(`[EVA:SDK] stored user token has expired: ${token}`)();
          const expiredAction: IEvaUserTokenExpiredAction = {
            type: 'USER_TOKEN_EXPIRED',
            payload: { token },
          };
          this.store.dispatch(expiredAction);

          const unsetTokenAction = createCurrentUserTokenAction();
          this.store.dispatch(unsetTokenAction);
          logger.warn(`User token ${token} has expired. Clearing current user state`)();
          await unsetTokenAction.payload.promiseChain;

          if (isFunction(this.userTokenExpired)) {
            this.userTokenExpired(token);
          }
        } else {
          logger.log(`[EVA:SDK] non-stored user token has expired: ${token}`)();
        }
      }

      // IMPORTANT: Do not return a response here or any other interceptors will fail to fire
    },
  };
}
